/**
 * API endpoint string-builders for `@/api/*` modules.
 *
 * Ported out of `apps/web/src/_legacy/utils/Endpoints.ts` (which pulled in
 * `@fortawesome/*` to attach nav-menu icons to every entry). This file keeps
 * only the React API URLs the in-shell api modules need, plus the Old Stack
 * (Angular) link base for screens not yet replatformed.
 */

import { oldStackUrl } from '@/lib/config';

export const endpoints = {
  /**
   * Links back into the Old Stack AngularJS host (VITE_ANGULAR_BASE_URL) for
   * screens not yet replatformed into apps/web. Pages that previously called
   * `endpoints.angular.order(...)` build their target from `oldStackUrl`.
   */
  oldStack: {
    base: (): string => oldStackUrl(''),
    order: (eventId: string, orderId: string): string =>
      oldStackUrl(`/#/events/${eventId}/orders/${orderId}`),
    /**
     * Per-order action screens still served by the Old Stack Angular host.
     * Ported from `Web.React/src/utils/Endpoints.ts` (endpoints.angular.orderActions.*)
     * for the Orders grid floating toolbar.
     */
    orderActions: {
      operations: (eventId: string, orderId: string): string =>
        oldStackUrl(`/#/events/${eventId}/orders/${orderId}/operations`),
      materials: (eventId: string, orderId: string): string =>
        oldStackUrl(`/#/events/${eventId}/orders/${orderId}/materials`),
      jointIntegrity: (eventId: string, orderId: string): string =>
        oldStackUrl(`/#/events/${eventId}/orders/${orderId}/jointintegrity`),
      estimateApprovals: (eventId: string, orderId: string): string =>
        oldStackUrl(`/#/events/${eventId}/orders/${orderId}/estimateapproval`),
      picturesAndAttachments: (eventId: string, orderId: string): string =>
        oldStackUrl(`/#/events/${eventId}/orders/${orderId}/attachments`),
      workPackEditor: (eventId: string, orderId: string): string =>
        oldStackUrl(`/#/events/${eventId}/orders/${orderId}/workpack`),
      bulkCopy: (eventId: string, orderId: string): string =>
        oldStackUrl(`/#/events/${eventId}/orders/${orderId}/bulkcopy`),
    },
  },
  event: {
    base: '/api/events',
    lockedOrders: (eventId: string): string => `/api/events/${eventId}/lockedorders`,
  },
  customDropDownList: {
    /**
     * Legacy Web API: `POST /api/customdropdownlist/getitems` with
     * `{ view, scopeIdentifiers, hasLocalCache }`. Returns CustomDropDownList
     * rows ({ id, name, code, view, ... }). The `view` is the scope name (e.g.
     * `LabourRotation` / `LabourCategory`). No regen needed — hand-called.
     */
    getItems: '/api/customdropdownlist/getitems',
  },
  tradeRate: {
    /**
     * Legacy Web API: the available `TimeCodeRate`s for an order + trade. The
     * Dayworks inline grid uses these to populate the `labourRateId` selector;
     * the server resolves Rate/Cost from the chosen rate (Rate is server-owned).
     */
    getTimeCodeRatesByOrder: (orderId: string, tradeId: string): string =>
      `/api/traderate/gettimecoderatesbyorder?orderId=${encodeURIComponent(
        orderId,
      )}&tradeId=${encodeURIComponent(tradeId)}`,
  },
  trade: {
    /**
     * New-stack Core.WebAPI `TradeController` (`GET /api/trade/gettrades`): the
     * full trade lookup that populates the Dayworks inline grid's Trade
     * selector. A line's trade scopes its available TimeCodeRates (see
     * `tradeRate` above) so the Trade must be chosen before a Rate is offered.
     */
    getTrades: '/api/trade/gettrades',
  },
  resource: {
    /**
     * New-stack Core.WebAPI `ResourceController`
     * (`GET /api/resource/getresourcesbytrade?tradeId={guid}`): the resources
     * associated with a trade, cascading from the Dayworks inline grid's Trade
     * selection to populate a line's Resource selector. Empty/unknown trade ⇒ [].
     */
    getResourcesByTrade: (tradeId: string): string =>
      `/api/resource/getresourcesbytrade?tradeId=${encodeURIComponent(tradeId)}`,
  },
  fileImport: {
    /**
     * Legacy generic-importer rail. The Dayworks inline grid posts a CSV/XLSX
     * file as multipart under the `LabourServiceInlineGrid` definition. The
     * definition name is the route discriminator the GenericImporter switches on.
     */
    upload: (definition: string): string => `/api/fileimports/${encodeURIComponent(definition)}`,
  },
  links: {
    /**
     * New-stack `LinksController` (`[Route("api/links")]`): typeahead + preview
     * for `link`/`personnel` fields. `fieldName` is the link *type* (e.g.
     * `Order_Supervisor`), not the draft property name.
     */
    search: (fieldName: string, query: string): string =>
      `/api/links/search/${encodeURIComponent(fieldName)}?query=${encodeURIComponent(query)}`,
    preview: (fieldName: string, id: string): string =>
      `/api/links/${encodeURIComponent(fieldName)}/${encodeURIComponent(id)}`,
  },
  localisation: {
    base: '/api/localisation',
    fieldsByFieldSet: (fieldSetName: string, eventId?: string): string =>
      `/api/localisation/field-sets/${encodeURIComponent(fieldSetName)}/fields${
        eventId ? `?eventId=${encodeURIComponent(eventId)}` : ''
      }`,
    podsByFieldSet: (fieldSetName: string, eventId?: string): string =>
      `/api/localisation/field-sets/${encodeURIComponent(fieldSetName)}/pods${
        eventId ? `?eventId=${encodeURIComponent(eventId)}` : ''
      }`,
  },
  orders: {
    apiBase: '/api/orders',
    search: (searchQuery: string): string =>
      `/api/orders/search?searchQuery=${encodeURIComponent(searchQuery)}`,
    getOrderNumber: (orderId: string): string => `/api/orders/${orderId}/ordernumber`,
    getAllOrdersForEvent: (query: string, eventId?: string): string => {
      const params = new URLSearchParams(query);
      if (eventId) params.append('eventId', eventId);
      params.append('$count', 'true');
      return `/odata/orders?${params.toString()}`;
    },
    deleteOrder: (orderId: string): string => `/api/orders/${orderId}`,
    getOrderById: (orderId: string): string => `/api/orders/${orderId}`,
    getOrderHistory: (orderId: string): string => `/api/orders/${orderId}/history`,
    getOrderStatuses: (eventId: string): string => `/api/orders/statuses/${eventId}`,
    transferOrders: (targetEventId: string): string =>
      `/api/orders/transfer?targetEventId=${targetEventId}`,
    addToWorkPackage: (workPackageId: string): string =>
      `/api/orders/workpackages/${workPackageId}`,
  },
  stockList: {
    /**
     * New-stack Core.WebAPI `StockListApiController` (FG-05): the contract's
     * stock catalogue for the material picker. Empty term = browse mode (the
     * server returns the capped default list), so `term` is omitted when blank.
     */
    search: (orderId: string, serviceType: number, term: string): string => {
      const params = new URLSearchParams({ orderId, serviceType: String(serviceType) });
      if (term) params.append('term', term);
      return `/api/stock-list/search?${params.toString()}`;
    },
  },
  sites: {
    base: '/api/sites',
    odata: (query: string = ''): string => {
      const params = new URLSearchParams(query);
      params.append('$count', 'true');
      return `/odata/sites?${params.toString()}`;
    },
    getSite: (siteId: string): string => `/api/sites/${siteId}`,
    getPlants: (siteId: string): string => `/api/sites/${siteId}/plants`,
    getAreas: (plantId: string): string => `/api/sites/plants/${plantId}/areas`,
    getPlantsForBlinds: '/api/sites/plants/plantsforblinds',
    getAreasForBlinds: '/api/sites/plants/areas/areasforblinds',
  },
  siteTimeCodeManagement: {
    timeTypesOData: '/odata/timetypes',
    timeCodesOData: '/odata/timecodes',
  },
  permissions: {
    base: '/api/permissions',
  },
  valuation: {
    base: (query: string): string => {
      const params = new URLSearchParams(query);
      params.append('$count', 'true');
      return `/odata/valuations?${params.toString()}`;
    },
    request: '/api/valuations/request',
    delete: (valuationId: string): string => `/api/valuations/${valuationId}`,
    sendToErp: '/api/valuations/sendtoerp',
  },
  valuationEntry: {
    base: (query: string, valuationId: string): string => {
      const params = new URLSearchParams(query);
      if (valuationId) params.append('valuationId', valuationId);
      params.append('$count', 'true');
      return `/odata/valuationentries?${params.toString()}`;
    },
  },
  settlementSheet: {
    base: '/api/settlementsheets',
    getForEvent: (eventId: string): string => `/api/settlementsheets/${eventId}`,
    refreshSettlementSheets: '/api/settlementsheets/refresh',
  },
  jobRevisions: {
    getRevisions: (query: string, orderId?: string): string => {
      const params = new URLSearchParams(query);
      if (orderId) {
        const orderFilter = `orderId eq ${orderId}`;
        const existingFilter = params.get('$filter');
        if (existingFilter) {
          params.set('$filter', `(${existingFilter}) and (${orderFilter})`);
        } else {
          params.set('$filter', orderFilter);
        }
      }
      params.append('$count', 'true');
      return `/odata/JobRevisions?${params.toString()}`;
    },
    getRevisionById: (revisionId: string): string => `/odata/JobRevisions(${revisionId})`,
  },
};

export const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';
